import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import toast from 'react-hot-toast'
import { apiFetch } from '../lib/api'

export interface BankAccount {
  id: number
  bank_name: string
  bank_icon: string
  bank_color: string
  account_name: string
  account_number?: string
  account_type: 'savings' | 'fixed' | 'current'
  balance: number
  currency: string
  interest_rate: number
  notes?: string
  savings_goal?: number | null
  created_at: string
  updated_at: string
}

export type BankAccountInput = Omit<BankAccount, 'id' | 'created_at' | 'updated_at'>

interface BankingState {
  accounts: BankAccount[]
  loading: boolean
  loaded: boolean
  loadAccounts: (force?: boolean) => Promise<void>
  createAccount: (data: BankAccountInput) => Promise<void>
  updateAccount: (id: number, data: Partial<BankAccountInput>) => Promise<void>
  deleteAccount: (id: number) => Promise<void>
  transferBetweenAccounts: (fromId: number, toId: number, amount: number) => Promise<void>
}

export const useBankingStore = create<BankingState>()(
  devtools(
    (set, get) => ({
      accounts: [],
      loading: false,
      loaded: false,

      loadAccounts: async (force = false) => {
        const { loading, loaded } = get()
        if (!force && (loaded || loading)) return
        set({ loading: true })
        try {
          const res = await apiFetch('/api/bank-accounts')
          if (!res.ok) throw new Error('Failed to load bank accounts')
          set({ accounts: await res.json(), loaded: true })
        } catch {
          toast.error('Failed to load bank accounts')
        } finally {
          set({ loading: false })
        }
      },

      createAccount: async (data) => {
        try {
          const res = await apiFetch('/api/bank-accounts', { method: 'POST', body: JSON.stringify(data) })
          if (!res.ok) { const e = await res.json(); throw new Error(e.error) }
          const created = await res.json()
          set(state => ({ accounts: [...state.accounts, created] }))
          toast.success('Bank account added')
        } catch (err: any) {
          toast.error(err.message || 'Failed to add bank account')
          throw err
        }
      },

      updateAccount: async (id, data) => {
        try {
          const res = await apiFetch(`/api/bank-accounts/${id}`, { method: 'PUT', body: JSON.stringify(data) })
          if (!res.ok) { const e = await res.json(); throw new Error(e.error) }
          const updated = await res.json()
          set(state => ({ accounts: state.accounts.map(a => a.id === id ? updated : a) }))
          toast.success('Bank account updated')
        } catch (err: any) {
          toast.error(err.message || 'Failed to update bank account')
          throw err
        }
      },

      deleteAccount: async (id) => {
        try {
          const res = await apiFetch(`/api/bank-accounts/${id}`, { method: 'DELETE' })
          if (!res.ok) { const e = await res.json(); throw new Error(e.error) }
          set(state => ({ accounts: state.accounts.filter(a => a.id !== id) }))
          toast.success('Bank account deleted')
        } catch (err: any) {
          toast.error(err.message || 'Failed to delete bank account')
          throw err
        }
      },

      transferBetweenAccounts: async (fromId, toId, amount) => {
        try {
          const res = await apiFetch('/api/bank-accounts/transfer', {
            method: 'POST',
            body: JSON.stringify({ from_account_id: fromId, to_account_id: toId, amount }),
          })
          if (!res.ok) { const e = await res.json(); throw new Error(e.error) }
          const { from, to } = await res.json()
          set(state => ({
            accounts: state.accounts.map(a => {
              if (a.id === fromId) return from
              if (a.id === toId) return to
              return a
            }),
          }))
          toast.success('โอนเงินสำเร็จ')
        } catch (err: any) {
          toast.error(err.message || 'โอนเงินไม่สำเร็จ')
          throw err
        }
      },
    }),
    { name: 'banking-store' }
  )
)
